import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import MaintenanceRecordForm from "../../components/maintenance/MaintenanceRecordForm";
import {
  fetchMaintenanceRecord,
  fetchMaintenanceRecordSuccess,
  fetchMaintenanceRecordFailure,
  createMaintenanceRecord,
  createMaintenanceRecordSuccess,
  createMaintenanceRecordFailure,
  updateMaintenanceRecord,
  updateMaintenanceRecordSuccess,
  updateMaintenanceRecordFailure,
  recordServiceVisit,
  recordServiceVisitSuccess,
  recordServiceVisitFailure,
  fetchMachines,
  fetchMachinesSuccess,
  fetchMachinesFailure,
  fetchServiceProviders,
  fetchServiceProvidersSuccess,
  fetchServiceProvidersFailure,
  clearCurrentRecord,
} from "../../store/maintenance/actions";

const MaintenanceRecordFormContainer = ({
  records,
  machines,
  serviceProviders,
  fetchMaintenanceRecord,
  createMaintenanceRecord,
  updateMaintenanceRecord,
  recordServiceVisit,
  fetchMachines,
  fetchServiceProviders,
  clearCurrentRecord,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;
  const isServiceVisit = location.pathname.includes("service-visit");

  useEffect(() => {
    fetchMachines();
    fetchServiceProviders();
    if (isEdit) {
      fetchMaintenanceRecord(id);
    }
    return () => {
      clearCurrentRecord();
    };
  }, [id, isEdit, fetchMaintenanceRecord, fetchMachines, fetchServiceProviders, clearCurrentRecord]);

  const handleSubmit = useCallback(
    (data, serviceVisit) => {
      if (serviceVisit) {
        recordServiceVisit(data, (newRecord) => {
          navigate(`/maintenance/records/${newRecord.id || newRecord.data?.id}`);
        });
      } else if (isEdit) {
        updateMaintenanceRecord(id, data, () => {
          navigate(`/maintenance/records/${id}`);
        });
      } else {
        createMaintenanceRecord(data, (newRecord) => {
          navigate(`/maintenance/records/${newRecord.id}`);
        });
      }
    },
    [isEdit, id, createMaintenanceRecord, updateMaintenanceRecord, recordServiceVisit, navigate]
  );

  return (
    <MaintenanceRecordForm
      record={records.current}
      machines={machines.list}
      serviceProviders={serviceProviders.list}
      loading={records.loading || machines.loading || serviceProviders.loading}
      error={records.error}
      onSubmit={handleSubmit}
      isEdit={isEdit}
      isServiceVisit={isServiceVisit}
    />
  );
};

const mapStateToProps = (state) => ({
  records: state.maintenance.records,
  machines: state.maintenance.machines,
  serviceProviders: state.maintenance.serviceProviders,
});

const mapDispatchToProps = (dispatch) => ({
  fetchMaintenanceRecord: (id) => {
    dispatch(fetchMaintenanceRecord(id)).payload.then(
      (response) => {
        dispatch(fetchMaintenanceRecordSuccess(response.data));
      },
      (error) => {
        dispatch(fetchMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  createMaintenanceRecord: (data, callback) => {
    dispatch(createMaintenanceRecord(data)).payload.then(
      (response) => {
        dispatch(createMaintenanceRecordSuccess(response.data));
        if (callback) callback(response.data);
      },
      (error) => {
        dispatch(createMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  updateMaintenanceRecord: (id, data, callback) => {
    dispatch(updateMaintenanceRecord(id, data)).payload.then(
      (response) => {
        dispatch(updateMaintenanceRecordSuccess(response.data));
        if (callback) callback(response.data);
      },
      (error) => {
        dispatch(updateMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  recordServiceVisit: (data, callback) => {
    dispatch(recordServiceVisit(data)).payload.then(
      (response) => {
        dispatch(recordServiceVisitSuccess(response.data));
        if (callback) callback(response.data);
      },
      (error) => {
        dispatch(recordServiceVisitFailure(error.response?.data || error));
      }
    );
  },
  fetchMachines: () => {
    dispatch(fetchMachines(1, null, null, null, null, 9999)).payload.then(
      (response) => {
        dispatch(fetchMachinesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchMachinesFailure(error.response?.data || error));
      }
    );
  },
  fetchServiceProviders: () => {
    dispatch(fetchServiceProviders(1, null, null, 9999)).payload.then(
      (response) => {
        dispatch(fetchServiceProvidersSuccess(response.data));
      },
      (error) => {
        dispatch(fetchServiceProvidersFailure(error.response?.data || error));
      }
    );
  },
  clearCurrentRecord: () => {
    dispatch(clearCurrentRecord());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceRecordFormContainer);
